/**
 * audioLeader · v2.7.65+E243
 * ──────────────────────────
 * Same-origin AUDIO LEADER election. When a dispatcher has several Admin
 * Console windows/tabs open, a system notification sound must play in EXACTLY
 * ONE window instead of echoing 3-5×.
 *
 * How it works:
 *   • Every Admin Console tab that mounts the audio engine calls
 *     activateAudioLeader() to JOIN the election over a BroadcastChannel.
 *   • Each participant heartbeats { id, lastActive, focused } ~every 1.5s.
 *   • The LEADER is the FOCUSED participant with the freshest `lastActive`
 *     (tie-break by id). A focused tab keeps bumping lastActive=now, so the
 *     most-recently active/focused window leads. If NO window is focused, the
 *     freshest `lastActive` wins — a brand-new tab opens with lastActive=now,
 *     so the most-recently-opened window becomes leader.
 *   • If the leader tab closes (pagehide → 'leave', or heartbeat times out),
 *     the remaining tabs recompute and a new leader takes over automatically,
 *     so notifications never go silent.
 *
 * SCOPE SAFETY: non-participants (driver/rider apps, or any tab that never
 * activates) always report isAudioLeader() === true, so their audio is never
 * suppressed. The dedupe applies strictly to the Admin Console.
 *
 * State is a WINDOW SINGLETON (`window.__RTG_AUDIO_LEADER__`) so every module
 * instance — even if the bundler/HMR evaluates this file more than once —
 * shares ONE election. This module only decides "may THIS tab emit sound right
 * now"; it never touches audio files, alert logic, or notification triggers.
 */
const CHANNEL = "rtg_audio_leader";
const HEARTBEAT_MS = 1500;
const DEAD_MS = 4200;

function _state() {
  if (typeof window === "undefined") {
    return { selfId: "ssr", bc: null, activated: false, lastActive: 0, isLeader: false, hb: null, peers: new Map(), listeners: new Set() };
  }
  if (!window.__RTG_AUDIO_LEADER__) {
    window.__RTG_AUDIO_LEADER__ = {
      selfId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`,
      bc: null,
      activated: false,
      lastActive: Date.now(),
      isLeader: false,
      hb: null,
      peers: new Map(),      // id -> { lastActive, focused, at }
      listeners: new Set(),  // (isLeader:boolean) => void
    };
  }
  return window.__RTG_AUDIO_LEADER__;
}

function _focused() {
  try {
    return typeof document !== "undefined"
      && document.visibilityState === "visible"
      && (document.hasFocus ? document.hasFocus() : true);
  } catch { return true; }
}

function _channel(S) {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!S.bc) { try { S.bc = new BroadcastChannel(CHANNEL); } catch { S.bc = null; } }
  return S.bc;
}

function _post(S, type) {
  try {
    _channel(S)?.postMessage({ type, id: S.selfId, lastActive: S.lastActive, focused: _focused(), at: Date.now() });
  } catch { /* noop */ }
}

function _recompute(S) {
  const now = Date.now();
  for (const [id, p] of S.peers) { if (now - p.at > DEAD_MS) S.peers.delete(id); }

  const candidates = [{ id: S.selfId, lastActive: S.lastActive, focused: _focused() }];
  for (const [id, p] of S.peers) candidates.push({ id, lastActive: p.lastActive, focused: p.focused });

  const focused = candidates.filter((c) => c.focused);
  const pool = focused.length ? focused : candidates;
  let best = pool[0];
  for (const c of pool) {
    if (c.lastActive > best.lastActive || (c.lastActive === best.lastActive && c.id < best.id)) best = c;
  }
  const next = best.id === S.selfId;
  if (next !== S.isLeader) {
    S.isLeader = next;
    S.listeners.forEach((cb) => { try { cb(S.isLeader); } catch { /* noop */ } });
  }
}

function _onMessage(S, ev) {
  const d = ev?.data;
  if (!d || d.id === S.selfId) return;
  if (d.type === "leave") { S.peers.delete(d.id); _recompute(S); return; }
  S.peers.set(d.id, { lastActive: d.lastActive || 0, focused: !!d.focused, at: Date.now() });
  if (d.type === "hello") _post(S, "hb");   // help the newcomer converge fast
  _recompute(S);
}

function _bump(S) {
  S.lastActive = Date.now();
  _post(S, "hb");
  _recompute(S);
}

/** Join the election. Idempotent. Call once from the Admin audio engine. */
export function activateAudioLeader() {
  const S = _state();
  if (S.activated || typeof window === "undefined") return;
  S.activated = true;
  S.lastActive = Date.now();

  const c = _channel(S);
  if (c) { try { c.addEventListener("message", (ev) => _onMessage(S, ev)); } catch { /* noop */ } }

  const bump = () => _bump(S);
  window.addEventListener("focus", bump);
  window.addEventListener("pointerdown", bump, true);
  window.addEventListener("keydown", bump, true);
  document.addEventListener("visibilitychange", () => { if (_focused()) _bump(S); });
  window.addEventListener("pagehide", () => _post(S, "leave"));
  window.addEventListener("beforeunload", () => _post(S, "leave"));

  S.hb = setInterval(() => {
    if (_focused()) S.lastActive = Date.now();   // keep the focused tab freshest
    _post(S, "hb");
    _recompute(S);
  }, HEARTBEAT_MS);

  _post(S, "hello");
  _recompute(S);
}

/** True when THIS tab may emit a system sound. Non-participants → always true. */
export function isAudioLeader() {
  const S = _state();
  if (!S.activated) return true;
  return S.isLeader;
}

/** Subscribe to leadership changes. Fires immediately with the current state. */
export function onLeadershipChange(cb) {
  const S = _state();
  S.listeners.add(cb);
  try { cb(S.isLeader); } catch { /* noop */ }
  return () => S.listeners.delete(cb);
}
